import { Edit } from "@mui/icons-material";
import { Box, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { Button, Show, TabbedShowLayout, useRecordContext, useTranslate } from "react-admin";

import { DataSource } from "../generated/graphql";
import { space } from "../theme/designTokens";
import { PatientRecord } from "../types/patient";
import { ActivityList } from "./ActivityList";
import { SourceSystemChip } from "./fields/SourceSystemChip";
import { PatientActivityGoalsTab } from "./PatientActivityGoals";
import PatientEdit from "./PatientEdit";
import { PatientInfoPanel } from "./PatientInfoPanel";
import { PatientProcedureList } from "./PatientProcedureList";
import UserAvatar from "./UserAvatar";

const PatientTitle = () => {
  const record = useRecordContext<PatientRecord>();
  if (!record) return null;

  return <Typography variant="body1">{`${record.organizationName} - Bewohner - ${record.name}`}</Typography>;
};

const PatientAvatar = () => {
  const record = useRecordContext<PatientRecord>();
  if (!record) return null;

  return <UserAvatar record={record} />;
};

const PatientShow = () => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const translate = useTranslate();

  const handleEditClick = () => {
    setIsEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setIsEditDialogOpen(false);
  };

  const PatientShowActions = ({ onEditClick }: { onEditClick: () => void }) => {
    const record = useRecordContext<PatientRecord>();
    const translate = useTranslate();
    const isEditable = record?.sourceSystem === DataSource.M4C || !record?.sourceSystem;
    const sourceLabel =
      record?.sourceSystem?.toLowerCase() &&
      translate(`Patient.fields.sourceSystem.${record.sourceSystem.toLowerCase()}`);

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: `${space.xs}px`, mb: `${space.xs}px` }}>
        <PatientAvatar />
        <SourceSystemChip translatePrefix="Patient.fields.sourceSystem" />
        <Box sx={{ ml: "auto", width: { xs: "100%", sm: "auto" } }}>
          <Tooltip
            title={
              isEditable
                ? ""
                : translate("common.editOnlyInSourceSystem", { source: sourceLabel || record?.sourceSystem })
            }
          >
            <span>
              <Button
                label="Bewohner bearbeiten"
                color="secondary"
                onClick={isEditable ? onEditClick : undefined}
                disabled={!isEditable}
              >
                <Edit />
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  return (
    <Show actions={<PatientShowActions onEditClick={handleEditClick} />} title={<PatientTitle />}>
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label={translate("tabs.information")}>
          <PatientInfoPanel />
        </TabbedShowLayout.Tab>

        <TabbedShowLayout.Tab label={translate("tabs.procedures")}>
          <PatientProcedureList />
        </TabbedShowLayout.Tab>

        <TabbedShowLayout.Tab label={translate("tabs.activityGoals")}>
          <PatientActivityGoalsTab />
        </TabbedShowLayout.Tab>

        <TabbedShowLayout.Tab label={translate("tabs.activityHistory")}>
          <ActivityList resource="PatientActivities" />
        </TabbedShowLayout.Tab>
      </TabbedShowLayout>
      {isEditDialogOpen && <PatientEdit open={isEditDialogOpen} onClose={handleEditClose} />}
    </Show>
  );
};

export default PatientShow;
